import { FC, memo, useMemo, useCallback } from 'react'
import { PageProps, ResponseLocals } from '../../common/types'
import { GetServerSideProps } from '../../redux/types'
import { getLocaleFromLanguage, getMessagesFromLanguage } from '../../i18n/utils'
import { deleteSession } from '../../utils/delete-session'
import Script from 'next/script'

import { getRoutesFromDomain } from '../../routes/utils'
import { BaseTransport } from '../../transports'

import { useUserDetails } from '../../hooks/users'
import { useTranslation } from '../../hooks/locale'
import Page from '../../components/common/page'
import PropertyDetailsPage from '../../components/property-details'
import { pageClient } from '../../clients'
import PropertyDetailsPageTransport, {
    PropertyDetailsPageParamsTransport,
} from '../../transports/property-details-page-transport'
import { encodeToBase64 } from '../../utils/encoding-utils'
import { PropertyDetails } from '../../models/property-details-page/property-details-model'
import { useRouting } from '../../hooks/routes'
import { useDateFormatter } from '../../hooks/date'
import usePriceFormatter from '../../hooks/price'
import { getProductSchemaForProperty, getVacationRentalSchemaForProperty } from '../../schema/property-details-page'
import { trackEvents } from '../../utils/track-utils'
import AnalyticsService from '../../services/analytics-service'
import { pdpPageActions } from '../../services/analytics-service/actions'
import { TrackingEventTypeEnum } from '../../common/enum'
import { PropertyDetailsContextProvider } from '../../provider/property-details-provider'
import { useRouter } from 'next/router'
import { getCategory } from '../../utils/category-utils'
import { incrementListingViewCount } from '../../utils/listing-view-count-utils'
import { useSetWishlistCount } from '../../hooks/wishlist'
import { useSessionEffect } from '../../hooks/session'

type Props = {
    fullUrl: string
    pathQueryParams: string
    locale: string
    data: BaseTransport<PropertyDetailsPageTransport>
    listingId: string
} & PageProps

const updateRecentlyViewedProperty = (listingId: string) => {
    try {
        incrementListingViewCount(listingId)
    } catch (error) {
        console.error('Failed to update recently viewed property:', error)
    }
}

const PropertyDetailsPageContainer: FC<Props> = ({ fullUrl, pathQueryParams, data, listingId }) => {
    const propertyData: PropertyDetailsPageTransport | null = data?.data
    const { userDetails } = useUserDetails({ additionalDetails: true })
    const isLoggedIn = !!userDetails?.id
    const { query } = useRouter()
    const { t } = useTranslation()
    const { format } = useDateFormatter()
    const { r } = useRouting()
    const priceFormatter = usePriceFormatter()
    const propertyDetailsFormatter = useMemo(() => {
        return new PropertyDetails(t, r, format, priceFormatter)
    }, [t, r, format, priceFormatter])

    useSetWishlistCount()

    const propertyDetails = useMemo(() => {
        if (propertyData) {
            return propertyDetailsFormatter.getData(
                propertyData.expose,
                propertyData.object,
                propertyData.listingConfig,
                propertyData.customerObjects,
                propertyData.breadcrumbs,
                propertyData.bathroomCount ?? 0,
            )
        }
        return null
    }, [propertyDetailsFormatter, propertyData])

    const isComboListing = propertyDetails?.general.isEntity || false

    const vacationRentalSchemaForProperty = useMemo(
        () =>
            getVacationRentalSchemaForProperty({
                propertyDetails,
                vacationRegionNode: propertyData?.vacationRegionNode,
                objectDetails: propertyData?.object,
                rooms: propertyData?.expose.objectRooms,
                prices: propertyData?.price,
                expose: propertyData?.expose,
                t,
            }),
        [propertyData, t],
    )

    const trackExposeEvents = useCallback(() => {
        const trackingEvents = []
        const eventValue = Number(listingId)
        if (listingId) {
            trackingEvents.push({
                eventType: TrackingEventTypeEnum.EVENT_EXPOSE_VIEW,
                eventValue,
            })
        }
        if (query?.tp) {
            trackingEvents.push({
                eventType: TrackingEventTypeEnum.EVENT_TOP_PROMOTION_CLICK,
                eventValue,
            })
        }
        if (query?.of) {
            trackingEvents.push({
                eventType: TrackingEventTypeEnum.EVENT_OFFER_CLICK,
                eventValue,
            })
        }
        if (query?.ptp_srl) {
            trackingEvents.push({
                eventType: TrackingEventTypeEnum.EVENT_TOP_PROMOTION_VIEW,
                eventValue,
            })
        }
        trackEvents(trackingEvents)
    }, [listingId, query?.tp, query?.of, query?.ptp_srl])

    const productSchemaForProperty = useMemo(
        () =>
            getProductSchemaForProperty({
                propertyDetails,
                prices: propertyData?.price,
            }),
        [propertyData],
    )

    useSessionEffect(() => {
        if (listingId) {
            updateRecentlyViewedProperty(listingId)
        }
        AnalyticsService.trackEvent({
            category: getCategory(isComboListing),
            action: pdpPageActions.PDP_PAGE_OPENED,
        })
        const timeoutId = setTimeout(() => {
            trackExposeEvents()
        }, 0)

        return () => clearTimeout(timeoutId)
    }, [listingId, trackExposeEvents, isComboListing])

    if (!propertyData) {
        return null
    }

    return (
        <Page head={propertyData.head}>
            {!!vacationRentalSchemaForProperty && (
                <Script
                    id="vacation-rental-schema"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={vacationRentalSchemaForProperty}
                />
            )}
            {!!productSchemaForProperty && (
                <Script
                    id="product-schema"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={productSchemaForProperty}
                />
            )}
            <PropertyDetailsContextProvider propertyDetails={propertyDetails}>
                <PropertyDetailsPage
                    userDetails={userDetails}
                    isLoggedIn={isLoggedIn}
                    url={fullUrl}
                    pathQueryParams={pathQueryParams}
                    navigationSection={propertyData.staticData.navigationSection}
                    footerSection={propertyData.staticData.footerSection}
                    regionNode={propertyData.regionNode}
                    listingId={listingId}
                    propertyDetailsFormatter={propertyDetailsFormatter}
                />
            </PropertyDetailsContextProvider>
        </Page>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req, res, query }) => {
    const { user, device } = res.locals as ResponseLocals
    const sitePathname = req.originalUrl || req.path
    const requestUserConfig = deleteSession(user)
    const protocol = req.header('x-forwarded-proto') || req.protocol || 'http'
    const host = req.header('host')
    const fullUrl = `${protocol}://${host}${sitePathname}`
    const locale = getLocaleFromLanguage(user.language)
    const { listingId } = req.params as unknown as PropertyDetailsPageParamsTransport
    const preview = !!query.preview
    const params: PropertyDetailsPageParamsTransport = {
        filter: encodeToBase64(req.originalUrl),
        preview,
        listingId,
    }

    const data = await pageClient.getPropertyDetailsPage({ user, params: params })
    if (data.status === 404) {
        return {
            notFound: true,
        }
    }
    return {
        props: {
            device,
            requestUserConfig: requestUserConfig,
            messages: await getMessagesFromLanguage(user.language),
            routes: await getRoutesFromDomain(user.domain),
            listingId,
            fullUrl,
            pathQueryParams: req.originalUrl,
            locale,
            data,
            originalPath: req.originalPath,
            originalQuery: req.originalQuery,
            originalUrl: req.originalUrl,
        },
    }
}

export default memo(PropertyDetailsPageContainer)
